// The last look at purchase caps before an order leaves.
//
// The product page caps the quantity box, the card's quick add counts what is
// already held, and the cart drawer disables + at the ceiling. All three are
// answers given while the shopper is looking, and all three can be got round:
// a cart built in another tab, a page left open while the cap changed, a
// quantity typed straight into the cart page and submitted.
//
// So the cart is read once more here, over the wire, and any line above its cap
// is brought back down before checkout opens. The shopper is told and left on
// the page, because their total is not what it was a moment ago and they should
// see that before they pay rather than after.
//
// This is still browser-side. It stops shoppers, not software: someone posting
// straight at /cart/add.js never runs any of it. For that, only a Shopify
// Function validating the cart at checkout is real enforcement. What backs this
// up in practice is that every order is checked by a pharmacist before it is
// dispensed.
(() => {
  const root = () => window.Shopify?.routes?.root || '/';

  const config = document.getElementById('cart-drawer');
  const cappedTag = (config?.dataset.limitTag || '').toLowerCase().trim();
  const cappedQty = Number(config?.dataset.limitQty || 0);
  const note = config?.dataset.limitNote || '';

  if (!cappedTag || !cappedQty) return;

  // Product lookups are cached for the life of the page. A shopper who reaches
  // checkout twice does not pay for the same lookups twice.
  const seen = new Map();

  const capFor = async (handle) => {
    if (seen.has(handle)) return seen.get(handle);

    const answer = await fetch(`${root()}products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((product) => {
        if (!product || !Array.isArray(product.tags)) return null;
        return product.tags.some((t) => String(t).toLowerCase().trim() === cappedTag) ? cappedQty : null;
      })
      .catch(() => null);

    seen.set(handle, answer);
    return answer;
  };

  const check = async () => {
    const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    if (!cart || !cart.items.length) return null;

    // A variant can sit on more than one line when the lines carry different
    // properties, and the cap is on the order, not on the line. Counted per
    // variant so three plus one is four and is caught.
    const held = new Map();
    for (const item of cart.items) {
      const key = String(item.variant_id);
      const row = held.get(key) || { handle: item.handle, title: item.product_title, quantity: 0 };
      row.quantity += item.quantity;
      held.set(key, row);
    }

    const over = [];
    await Promise.all(
      [...held.entries()].map(async ([id, row]) => {
        const cap = await capFor(row.handle);
        if (cap !== null && row.quantity > cap) over.push({ id, cap, ...row });
      })
    );

    if (!over.length) return null;

    // update.js keyed by variant id sets the total across every line of that
    // variant in one call, which is exactly the quantity that was measured.
    const updates = {};
    for (const row of over) updates[row.id] = row.cap;

    const updated = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    if (updated && window.StLukesCart?.refresh) {
      window.StLukesCart.refresh().catch(() => {});
    }

    if (over.length === 1) {
      const only = over[0];
      return note || `We limit ${only.title} to ${only.cap} per order. Your cart has been updated.`;
    }

    return note
      ? `${note} Your cart has been updated.`
      : 'Some items are limited per order. Your cart has been updated.';
  };

  window.StLukesCheckoutChecks = window.StLukesCheckoutChecks || [];
  window.StLukesCheckoutChecks.push(check);
})();
